import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  CalendarDays,
  Flame,
  Thermometer,
  Snowflake,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PipelineHealth, STAGE_LABELS, PRIORITY_COLORS } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Stage colors
const STAGE_COLORS: Record<string, string> = {
  NEW: 'bg-blue-500',
  CONTACTED: 'bg-cyan-500',
  DISCUSSION: 'bg-purple-500',
  PROPOSAL: 'bg-orange-500',
  NEGOTIATION: 'bg-amber-500',
  WON: 'bg-green-500',
};

interface PipelineHealthSectionProps {
  pipeline: PipelineHealth;
  isLoading?: boolean;
  onStageClick?: (stage: string) => void;
  onPriorityClick?: (priority: string) => void;
}

export const PipelineHealthSection: React.FC<PipelineHealthSectionProps> = ({
  pipeline,
  isLoading,
  onStageClick,
  onPriorityClick,
}) => {
  // Calculate max count for scaling the bars
  const maxCount = Math.max(...pipeline.byStage.map((s) => s.count), 1);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pipeline Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 bg-gray-100 animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Pipeline Health
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stage Breakdown */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 mb-3">By Stage</h4>
          <div className="space-y-2">
            {pipeline.byStage
              .filter((s) => s.stage !== 'WON' && s.stage !== 'LOST')
              .map((stage, index) => (
                <motion.div
                  key={stage.stage}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-3 rounded-md px-1 py-0.5 transition-colors ${onStageClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={() => onStageClick?.(stage.stage)}
                  role={onStageClick ? 'button' : undefined}
                  tabIndex={onStageClick ? 0 : undefined}
                  onKeyDown={onStageClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStageClick(stage.stage); } } : undefined}
                  aria-label={`${STAGE_LABELS[stage.stage] || stage.stage}: ${stage.count} leads, ${formatCurrency(stage.value)}${onStageClick ? ' - click to view' : ''}`}
                >
                  <div className="w-24 text-sm text-gray-600 truncate">
                    {STAGE_LABELS[stage.stage] || stage.stage}
                  </div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(stage.count / maxCount) * 100}%` }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className={`h-full ${STAGE_COLORS[stage.stage] || 'bg-gray-400'} rounded-full flex items-center justify-end px-2`}
                    >
                      {stage.count > 0 && (
                        <span className="text-xs font-medium text-white">
                          {stage.count}
                        </span>
                      )}
                    </motion.div>
                  </div>
                  <div className="w-24 text-right text-sm font-medium text-gray-700">
                    {formatCurrency(stage.value)}
                  </div>
                </motion.div>
              ))}
          </div>
        </div>

        {/* Expected Closures */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-50 border border-blue-100 rounded-lg p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">
                Expected This Week
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-blue-800">
                {pipeline.expectedWeek.count}
              </span>
              <span className="text-sm text-blue-600">deals</span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              {formatCurrency(pipeline.expectedWeek.value)}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-indigo-50 border border-indigo-100 rounded-lg p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">
                Expected This Month
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-indigo-800">
                {pipeline.expectedMonth.count}
              </span>
              <span className="text-sm text-indigo-600">deals</span>
            </div>
            <p className="text-sm text-indigo-600 mt-1">
              {formatCurrency(pipeline.expectedMonth.value)}
            </p>
          </motion.div>
        </div>

        {/* Priority Distribution */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 mb-3">By Priority</h4>
          <div className="flex gap-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className={`flex-1 bg-red-50 border border-red-100 rounded-lg p-3 text-center ${onPriorityClick ? 'cursor-pointer hover:shadow-md hover:border-red-300 transition-all' : ''}`}
              onClick={() => onPriorityClick?.('HOT')}
              role={onPriorityClick ? 'button' : undefined}
              tabIndex={onPriorityClick ? 0 : undefined}
              onKeyDown={onPriorityClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPriorityClick('HOT'); } } : undefined}
              aria-label={onPriorityClick ? `View ${pipeline.byPriority.HOT} hot priority leads` : undefined}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">Hot</span>
              </div>
              <span className="text-2xl font-bold text-red-800">
                {pipeline.byPriority.HOT}
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.55 }}
              className={`flex-1 bg-orange-50 border border-orange-100 rounded-lg p-3 text-center ${onPriorityClick ? 'cursor-pointer hover:shadow-md hover:border-orange-300 transition-all' : ''}`}
              onClick={() => onPriorityClick?.('WARM')}
              role={onPriorityClick ? 'button' : undefined}
              tabIndex={onPriorityClick ? 0 : undefined}
              onKeyDown={onPriorityClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPriorityClick('WARM'); } } : undefined}
              aria-label={onPriorityClick ? `View ${pipeline.byPriority.WARM} warm priority leads` : undefined}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Thermometer className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700">Warm</span>
              </div>
              <span className="text-2xl font-bold text-orange-800">
                {pipeline.byPriority.WARM}
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className={`flex-1 bg-blue-50 border border-blue-100 rounded-lg p-3 text-center ${onPriorityClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition-all' : ''}`}
              onClick={() => onPriorityClick?.('COLD')}
              role={onPriorityClick ? 'button' : undefined}
              tabIndex={onPriorityClick ? 0 : undefined}
              onKeyDown={onPriorityClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPriorityClick('COLD'); } } : undefined}
              aria-label={onPriorityClick ? `View ${pipeline.byPriority.COLD} cold priority leads` : undefined}
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Snowflake className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700">Cold</span>
              </div>
              <span className="text-2xl font-bold text-blue-800">
                {pipeline.byPriority.COLD}
              </span>
            </motion.div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
